import React, { useEffect, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';

import Search from '@rover/icons/svg/search.svg';
import { Box, Button } from '@rover/kibble/core';
import Banner from '@rover/kibble/official-patterns/Banner';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import { PetCompatibilityAnswerEnum } from '@rover/rsdk/src/apiClient/latest/model/petCompatibilityAnswerEnum';
import type { PetCompatibilityQuestionEnum as Question } from '@rover/rsdk/src/apiClient/latest/model/petCompatibilityQuestionEnum';
import { Logger } from '@rover/rsdk/src/modules/Logging';

import type { AnsweredQuestion } from './usePetCompatibilityAnswers';

type Props = {
  answered: AnsweredQuestion[];
  petName: string | null;
  hasSavedToPets: boolean;
  onSearchNow: () => void;
  onClose: () => void;
};

const formatOrList = (items: string[], locale: string): string => {
  try {
    return new Intl.ListFormat(locale, { type: 'disjunction' }).format(items);
  } catch (e) {
    Logger.info('Pet compatibility outcome fell back to an English list', {
      locale,
      errorMessage: (e as Error).message,
    });
    return new Intl.ListFormat('en', { type: 'disjunction' }).format(items);
  }
};

const PetCompatibilityOutcome = ({
  answered,
  petName,
  hasSavedToPets,
  onSearchNow,
  onClose,
}: Props): JSX.Element => {
  const { i18n, t } = useLingui();
  const withoutNouns: Record<Question, string> = {
    dogs: t({ message: 'dogs', context: 'We’ll show you sitters without ___ in the home' }),
    cats: t({ message: 'cats', context: 'We’ll show you sitters without ___ in the home' }),
    kids: t({ message: 'kids', context: 'We’ll show you sitters without ___ in the home' }),
  };
  const mayHaveNouns: Record<Question, string> = {
    dogs: t({ message: 'dogs', context: 'sitters who may have ___ in the home' }),
    cats: t({ message: 'cats', context: 'sitters who may have ___ in the home' }),
    kids: t({ message: 'kids', context: 'sitters who may have ___ in the home' }),
  };
  const noQuestions = answered
    .filter(({ answer }) => answer === PetCompatibilityAnswerEnum.no)
    .map(({ question }) => question);
  const hasNo = noQuestions.length > 0;
  const households = hasNo
    ? formatOrList(
        noQuestions.map((question) => withoutNouns[question]),
        i18n.locale
      )
    : formatOrList(
        answered.map(({ question }) => mayHaveNouns[question]),
        i18n.locale
      );
  const profileUpdated = petName && hasSavedToPets && t`We’ve updated ${petName}’s profile.`;
  const titleRef = useRef<HTMLElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  return (
    <Banner
      severity="success"
      title={
        <Box ref={titleRef} webAs="span" webProps={{ tabIndex: -1 }}>
          {hasNo
            ? t`Got it. We’ll show you sitters without ${households} in the home.`
            : t`Thanks! We’ll keep showing you sitters who may have ${households} in the home.`}
          {profileUpdated && <A11yHiddenBox webAs="span"> {profileUpdated}</A11yHiddenBox>}
        </Box>
      }
      containerProps={{ webProps: { 'aria-live': 'off' } }}
      onClose={onClose}
      FooterComponent={
        hasNo && (
          <Button icon={Search} onPress={onSearchNow}>
            {t`Search now`}
          </Button>
        )
      }
      testID="pet-compatibility-outcome"
    >
      {profileUpdated && (
        <Box webAs="span" webProps={{ 'aria-hidden': true }}>
          {profileUpdated}
        </Box>
      )}
    </Banner>
  );
};

export default PetCompatibilityOutcome;
